import React from "react";

// TP-Friendlies opponent-tier selector. The VOCABULARY is strictly the platform's
// existing league-tier taxonomy (DECISIONS D-004.4) — tiers 1/2/3 + the exclusive
// shortcuts "3 or lower" / "Any". Only the SELECTOR is net-new UI.
//
// Two modes share one component:
//   • "multi"  — numeric tiers accumulate into a RANGE (1+2 → 1–2). This is the
//     COMPOSER's model (tier_mode=range, tier_min/tier_max) — reused in M3.
//   • "single" — one tier at a time. This is the BROWSE FILTER's model, because the
//     M1 feed endpoint accepts a single `?tier=<int>` OVERLAP value, not a set. A
//     multi-select browse filter is not expressible against that contract, so the
//     filter uses single-select and maps straight to `?tier=` (no faked results).
//
// The selection RULES are pure functions (exported for unit tests): shortcuts are
// mutually exclusive with each other and with numeric tiers; picking a numeric
// clears any active shortcut.

// ── Pure reducers (tested directly) ──────────────────────────────────────────
// SINGLE value: "" | "1" | "2" | "3" | "three_or_lower". ("" === Any.)
export const toggleSingleTier = (current, tier) =>
  String(current) === String(tier) ? "" : String(tier);

export const selectSingleShortcut = (current, shortcut) => {
  // "any" always clears; "three_or_lower" toggles.
  if (shortcut === "any") return "";
  return current === shortcut ? "" : shortcut;
};

// MULTI value: { tiers: number[], shortcut: null | "three_or_lower" }.
// "Any" = { tiers: [], shortcut: null }.
export const emptyMulti = { tiers: [], shortcut: null };

export const toggleMultiTier = (current, tier) => {
  const t = Number(tier);
  const has = current.tiers.includes(t);
  const tiers = has ? current.tiers.filter((x) => x !== t) : [...current.tiers, t].sort((a, b) => a - b);
  return { tiers, shortcut: null }; // picking a numeric clears any shortcut
};

export const selectMultiShortcut = (current, shortcut) => {
  if (shortcut === "any") return { ...emptyMulti };
  // three_or_lower is exclusive: it clears numeric tiers, toggles itself off if re-picked.
  return { tiers: [], shortcut: current.shortcut === "three_or_lower" ? null : "three_or_lower" };
};

// Serialize a MULTI value to the backend `?tier=` param (fr-be-builder 11de66d):
// numeric tiers → comma list "1,2"; the shortcut → "3_or_lower"; empty → "" (omit).
export const serializeMultiTier = (value) => {
  if (!value) return "";
  if (value.shortcut === "three_or_lower") return "3_or_lower";
  return value.tiers.length ? value.tiers.join(",") : "";
};

// Parse the `?tier=` param back to a MULTI value (for the active filter state).
export const parseMultiTier = (str) => {
  const s = String(str || "").trim();
  if (!s || s === "any") return { ...emptyMulti };
  if (s === "3_or_lower" || s === "three_or_lower") return { tiers: [], shortcut: "three_or_lower" };
  const tiers = s
    .split(",")
    .map((x) => Number(x))
    .filter((n) => n >= 1 && n <= 3)
    .sort((a, b) => a - b);
  return { tiers, shortcut: null };
};

// Derive the display string ("Tier 1–2" / "Tier 3 or lower" / "Any level") the way
// the card renders, so the composer preview (M3) stays consistent with the server.
export const multiTierDisplay = (value) => {
  if (value.shortcut === "three_or_lower") return "Tier 3 or lower";
  if (!value.tiers.length) return "Any level";
  const lo = value.tiers[0];
  const hi = value.tiers[value.tiers.length - 1];
  return lo === hi ? `Tier ${lo}` : `Tier ${lo}-${hi}`;
};

// ── UI ────────────────────────────────────────────────────────────────────────
const pillClass = (active) =>
  `inline-flex items-center justify-center min-w-[36px] rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
    active ? "border-[#2283FF] bg-[#EAF3FF] text-[#1866CC]" : "border-[#E0E6EE] text-[#475467] hover:border-[#B5CCF0]"
  }`;

const TierSelector = ({
  mode = "single",
  value,
  onChange,
  showThreeOrLower = true,
  showAny = true,
}) => {
  const isMulti = mode === "multi";
  const val = value ?? (isMulti ? emptyMulti : "");

  const numericActive = (t) =>
    isMulti ? val.tiers.includes(Number(t)) : String(val) === String(t);
  const shortcutActive = (s) => (isMulti ? val.shortcut === s : String(val) === s);
  const anyActive = isMulti ? !val.tiers.length && !val.shortcut : !val;

  const onNumeric = (t) =>
    onChange(isMulti ? toggleMultiTier(val, t) : toggleSingleTier(val, t));
  const onShortcut = (s) =>
    onChange(isMulti ? selectMultiShortcut(val, s) : selectSingleShortcut(val, s));

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Opponent level">
      {[1, 2, 3].map((t) => (
        <button key={t} type="button" className={pillClass(numericActive(t))} onClick={() => onNumeric(t)} aria-pressed={numericActive(t)}>
          {t}
        </button>
      ))}
      {(showThreeOrLower || showAny) && <span className="mx-0.5 w-px h-5 bg-[#E4E9F0]" aria-hidden="true" />}
      {showThreeOrLower && (
        <button
          type="button"
          className={pillClass(shortcutActive("three_or_lower"))}
          onClick={() => onShortcut("three_or_lower")}
          aria-pressed={shortcutActive("three_or_lower")}
        >
          3 or lower
        </button>
      )}
      {showAny && (
        <button type="button" className={pillClass(anyActive)} onClick={() => onShortcut("any")} aria-pressed={anyActive}>
          Any
        </button>
      )}
    </div>
  );
};

export default TierSelector;
